
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface FollowBackSuggestion {
  id: string;
  full_name: string;
  username: string;
  avatar_url?: string;
}

export const useFollowBackSuggestions = () => {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState<FollowBackSuggestion[]>([]);
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchFollowBackSuggestions = async () => {
      if (!user) return;
      
      try {
        // Get users who follow me but I don't follow back
        const { data: followersNotFollowedBack, error } = await supabase
          .from('user_follows')
          .select(`
            follower_id,
            profiles!follower_id(
              id,
              full_name,
              username,
              avatar_url
            )
          `)
          .eq('following_id', user.id);
          
        if (error) throw error;
        
        const suggestions: FollowBackSuggestion[] = [];
        
        for (const follow of followersNotFollowedBack || []) {
          if (follow.profiles && typeof follow.profiles === 'object') {
            const profile = follow.profiles as any;
            
            // Check if I'm already following them back
            const { data: alreadyFollowing } = await supabase
              .from('user_follows')
              .select('*')
              .eq('follower_id', user.id)
              .eq('following_id', follow.follower_id)
              .single();
              
            if (!alreadyFollowing && !dismissedSuggestions.has(profile.id)) {
              suggestions.push({
                id: profile.id,
                full_name: profile.full_name || 'Unknown User',
                username: profile.username || 'username',
                avatar_url: profile.avatar_url
              });
            }
          }
        }
        
        setSuggestions(suggestions);
      } catch (error) {
        console.error('Error fetching follow back suggestions:', error);
      }
    };
    
    fetchFollowBackSuggestions();
  }, [user, dismissedSuggestions]);

  const dismissSuggestion = (userId: string) => {
    setDismissedSuggestions(prev => new Set(prev).add(userId));
    setSuggestions(prev => prev.filter(s => s.id !== userId));
  };

  return {
    suggestions,
    dismissSuggestion
  };
};
